import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Phone, MapPin, Calendar, Landmark,
  ShieldCheck, FileText, ExternalLink, AlertCircle,
  PlusCircle, X, CheckCircle2, TrendingUp,
} from 'lucide-react';
import { usersAPI } from '../../api/users';
import PageHeader from '../../components/ui/PageHeader';
import Button     from '../../components/ui/Button';
import Input      from '../../components/ui/Input';

// ── Helpers ──────────────────────────────────────────────────
const formatINR = (val) =>
  `₹${Number(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const formatDate = (val) => {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'long', year: 'numeric',
  });
};

const DOC_LABELS = {
  pan_card:        'PAN Card',
  aadhar_card:     'Aadhaar Card',
  aadhar:          'Aadhaar Card',
  passport:        'Passport',
  driving_license: 'Driving Licence',
  voter_id:        'Voter ID',
};
const docLabel = (type) =>
  DOC_LABELS[type] || type?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || 'Document';

const genderColor = {
  male:   { bg: 'rgba(59,130,246,0.15)',  text: '#93c5fd' },
  female: { bg: 'rgba(236,72,153,0.15)',  text: '#f9a8d4' },
  other:  { bg: 'rgba(139,92,246,0.15)',  text: '#c4b5fd' },
};

const EMPTY_PROFIT_FORM = { type: 'amount', value: '', note: '' };

// ── Sub-components ───────────────────────────────────────────
function Section({ icon: Icon, title, children }) {
  return (
    <div className="card flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg flex-shrink-0"
             style={{ background: 'rgba(212,160,23,0.15)' }}>
          <Icon size={14} style={{ color: '#d4a017' }} />
        </div>
        <h3 className="font-serif text-sm font-semibold text-white">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-xs flex-shrink-0" style={{ color: 'rgba(255,255,255,0.45)' }}>{label}</span>
      <span className="text-xs font-medium text-right text-white break-all">{value || '—'}</span>
    </div>
  );
}

function StatCard({ label, value, highlight }) {
  return (
    <div className="flex flex-col gap-1 rounded-2xl p-4"
         style={{
           background: highlight
             ? 'linear-gradient(135deg,#b8860b 0%,#d4a017 40%,#f0c040 100%)'
             : 'rgba(255,255,255,0.05)',
           border: highlight ? 'none' : '1px solid rgba(212,160,23,0.15)',
         }}>
      <p className="text-[10px] font-medium uppercase tracking-widest"
         style={{ color: highlight ? 'rgba(255,255,255,0.75)' : 'rgba(212,160,23,0.7)' }}>
        {label}
      </p>
      <p className="text-lg font-bold font-serif text-white">{value}</p>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="card flex flex-col items-center gap-3 py-6">
        <div className="skeleton h-20 w-20 rounded-full" />
        <div className="skeleton h-5 w-36 rounded" />
        <div className="skeleton h-3.5 w-24 rounded" />
      </div>
      <div className="grid grid-cols-3 gap-2">
        {[1,2,3].map(i => <div key={i} className="skeleton h-20 rounded-2xl" />)}
      </div>
      {[1,2,3].map(i => <div key={i} className="skeleton h-28 rounded-2xl" />)}
    </div>
  );
}

// ── Add Profit Sheet ─────────────────────────────────────────
function AddProfitSheet({ user, onClose, onSuccess }) {
  const [form,       setForm]       = useState(EMPTY_PROFIT_FORM);
  const [errors,     setErrors]     = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError,   setApiError]   = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
    if (apiError)     setApiError('');
  };

  const setType = (t) => {
    setForm((p) => ({ ...p, type: t, value: '' }));
    setErrors({});
    setApiError('');
  };

  const validate = () => {
    const errs = {};
    const num = parseFloat(form.value);
    if (!form.value || isNaN(num) || num < 0.01)
      errs.value = 'Enter a value of at least 0.01';
    if (form.type === 'percentage' && num > 100)
      errs.value = 'Percentage cannot exceed 100';
    return errs;
  };

  // POST /api/admin/users/{id}/profit
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    try {
      const { data } = await usersAPI.addProfit(user.id, {
        type:  form.type,
        value: parseFloat(form.value),
        note:  form.note.trim() || undefined,
      });

      if (data.status) {
        onSuccess(data.data); // pass credited_amount back to parent
        onClose();
      } else {
        setApiError(data.message || 'Failed to add profit.');
      }
    } catch (err) {
      setApiError(
        err?.response?.data?.message ||
        err?.response?.data?.errors?.value?.[0] ||
        'Something went wrong. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
      onClick={!submitting ? onClose : undefined}
    >
      <div
        className="w-full max-w-app animate-fade-up rounded-t-3xl p-6 pb-8"
        style={{
          background: 'linear-gradient(180deg,#0e2060 0%,#060f38 100%)',
          border: '1px solid rgba(212,160,23,0.2)',
          borderBottom: 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sheet header */}
        <div className="mb-5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl flex-shrink-0"
                 style={{ background: 'rgba(212,160,23,0.15)', border: '1px solid rgba(212,160,23,0.25)' }}>
              <TrendingUp size={16} style={{ color: '#d4a017' }} />
            </div>
            <div>
              <h3 className="font-serif text-base font-semibold text-white">Add Profit</h3>
              <p className="text-[11px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
                {user.name}
              </p>
            </div>
          </div>
          {!submitting && (
            <button onClick={onClose}
                    className="flex h-7 w-7 items-center justify-center rounded-xl"
                    style={{ background: 'rgba(255,255,255,0.08)' }}>
              <X size={14} className="text-white" />
            </button>
          )}
        </div>

        {/* API error */}
        {apiError && (
          <div className="mb-4 flex items-start gap-2 rounded-xl px-3 py-2.5 text-xs"
               style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5' }}>
            <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
            <span>{apiError}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">

          {/* Type toggle */}
          <div>
            <p className="input-label mb-2">Profit Type</p>
            <div className="grid grid-cols-2 gap-2">
              {['amount', 'percentage'].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className="rounded-xl py-2.5 text-sm font-semibold capitalize transition-all duration-150"
                  style={{
                    background: form.type === t
                      ? 'linear-gradient(135deg,#d4a017,#f0c040)'
                      : 'rgba(255,255,255,0.07)',
                    color: form.type === t ? '#fff' : 'rgba(255,255,255,0.55)',
                    border: form.type === t ? 'none' : '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  {t === 'amount' ? '₹ Fixed Amount' : '% Percentage'}
                </button>
              ))}
            </div>
          </div>

          {/* Value */}
          <Input
            label={form.type === 'amount' ? 'Amount (₹)' : 'Percentage (%)'}
            type="number"
            name="value"
            placeholder={form.type === 'amount' ? 'e.g. 5000' : 'e.g. 5'}
            value={form.value}
            onChange={handleChange}
            error={errors.value}
            min="0.01"
            step={form.type === 'amount' ? '0.01' : '0.01'}
            max={form.type === 'percentage' ? '100' : undefined}
          />

          {/* Note */}
          <div className="flex flex-col gap-1.5">
            <label className="input-label">Note (optional)</label>
            <textarea
              name="note"
              value={form.note}
              onChange={handleChange}
              placeholder="e.g. Monthly profit for January 2026"
              rows={2}
              maxLength={500}
              className="input-field resize-none"
            />
          </div>

          {/* Deposited balance hint for percentage */}
          {form.type === 'percentage' && (
            <p className="text-xs px-1" style={{ color: 'rgba(255,255,255,0.35)' }}>
              Percentage is calculated on total approved deposits:{' '}
              <span style={{ color: '#d4a017' }}>{formatINR(user.total_deposited)}</span>
            </p>
          )}

          <Button type="submit" variant="gold" fullWidth loading={submitting} className="mt-1">
            Add Profit
          </Button>

        </form>
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────
export default function UserDetailPage() {
  const { id } = useParams();

  const [user,       setUser]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [showProfit, setShowProfit] = useState(false);
  const [toast,      setToast]      = useState(null);

  const fetchUser = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await usersAPI.getById(id);
      if (data.status) setUser(data.data);
    } catch (err) {
      setError(
        err?.response?.status === 404
          ? 'User not found.'
          : err?.response?.data?.message || 'Failed to load user details.'
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // Update financials in-place after profit added
  const handleProfitSuccess = (profitData) => {
    const credited = parseFloat(profitData.credited_amount);
    setUser((prev) => {
      const newProfit  = parseFloat(prev.total_profit)  + credited;
      const newBalance = parseFloat(prev.total_balance) + credited;
      return {
        ...prev,
        total_profit:  newProfit.toFixed(2),
        total_balance: newBalance.toFixed(2),
      };
    });
    setToast({
      message: `₹${credited.toLocaleString('en-IN', { minimumFractionDigits: 2 })} profit added successfully`,
    });
  };

  // ── Loading ───────────────────────────────────────────────
  if (loading) {
    return (
      <div>
        <PageHeader title="User Detail" showBack />
        <DetailSkeleton />
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────
  if (error) {
    return (
      <div>
        <PageHeader title="User Detail" showBack />
        <div className="flex flex-col items-center justify-center gap-3 py-20 px-6">
          <div className="flex h-14 w-14 items-center justify-center rounded-full"
               style={{ background: 'rgba(239,68,68,0.12)' }}>
            <AlertCircle size={24} style={{ color: '#fca5a5' }} />
          </div>
          <p className="text-sm text-center" style={{ color: '#fca5a5' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  const gc       = genderColor[user.gender] || genderColor.other;
  const initials = user.name.split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase();

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="flex flex-col min-h-0">
      <PageHeader
        title={user.name}
        showBack
        right={
          <button
            onClick={() => setShowProfit(true)}
            className="flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold
                       transition-all active:scale-95"
            style={{
              background: 'rgba(212,160,23,0.15)',
              border: '1px solid rgba(212,160,23,0.3)',
              color: '#d4a017',
            }}
          >
            <PlusCircle size={13} />
            Add Profit
          </button>
        }
      />

      <div className="flex flex-col gap-4 p-4">

        {/* ── Success toast ── */}
        {toast && (
          <div className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm animate-fade-in"
               style={{ background: 'rgba(5,150,105,0.15)', border: '1px solid rgba(5,150,105,0.35)', color: '#6ee7b7' }}>
            <CheckCircle2 size={15} className="flex-shrink-0" />
            <span>{toast.message}</span>
          </div>
        )}

        {/* ── Profile hero card ── */}
        <div className="card flex flex-col items-center gap-3 py-6">
          {user.profile_photo ? (
            <img src={user.profile_photo} alt={user.name}
                 className="h-20 w-20 rounded-full object-cover"
                 style={{ border: '2px solid rgba(212,160,23,0.4)' }} />
          ) : (
            <div className="flex h-20 w-20 items-center justify-center rounded-full text-2xl font-bold text-white"
                 style={{ background: 'linear-gradient(135deg,#1448c4 0%,#0a2260 100%)',
                          border: '2px solid rgba(212,160,23,0.3)' }}>
              {initials}
            </div>
          )}

          <div className="flex flex-col items-center gap-1">
            <h2 className="font-serif text-xl font-bold text-white">{user.name}</h2>
            <div className="flex items-center gap-2">
              <span className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>{user.mobile}</span>
              <span className="inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium capitalize"
                    style={{ background: gc.bg, color: gc.text }}>
                {user.gender}
              </span>
            </div>
          </div>

          <div className="mt-1 w-full grid grid-cols-2 gap-x-4 gap-y-2 border-t pt-4"
               style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
            <div className="flex items-center gap-1.5">
              <Calendar size={12} style={{ color: '#d4a017' }} />
              <span className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>
                {formatDate(user.date_of_birth)}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <Calendar size={12} style={{ color: 'rgba(255,255,255,0.3)' }} />
              <span className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
                Joined {formatDate(user.registered_at)}
              </span>
            </div>
            {user.address && (
              <div className="col-span-2 flex items-start gap-1.5">
                <MapPin size={12} className="flex-shrink-0 mt-0.5" style={{ color: '#d4a017' }} />
                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>{user.address}</span>
              </div>
            )}
            {user.additional_phone && (
              <div className="flex items-center gap-1.5">
                <Phone size={12} style={{ color: '#d4a017' }} />
                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>{user.additional_phone}</span>
              </div>
            )}
          </div>
        </div>

        {/* ── Financial summary ── */}
        <div className="grid grid-cols-2 gap-2">
          <StatCard label="Deposited"  value={formatINR(user.total_deposited)} />
          <StatCard label="Profit"     value={formatINR(user.total_profit)} />
        </div>
        {parseFloat(user.total_withdrawn || 0) > 0 && (
          <div className="grid grid-cols-2 gap-2">
            <StatCard label="Withdrawn"  value={formatINR(user.total_withdrawn)} />
            <StatCard label="Balance"    value={formatINR(user.total_balance)} highlight />
          </div>
        )}
        {!(parseFloat(user.total_withdrawn || 0) > 0) && (
          <StatCard label="Balance" value={formatINR(user.total_balance)} highlight />
        )}

        {/* ── Bank Account ── */}
        <Section icon={Landmark} title="Bank Account">
          {user.bank_account ? (
            <div className="flex flex-col gap-2.5">
              <InfoRow label="Bank"           value={user.bank_account.bank_name} />
              <InfoRow label="Account Holder" value={user.bank_account.name} />
              <InfoRow label="Account No."    value={user.bank_account.account_number} />
              <InfoRow label="IFSC"           value={user.bank_account.ifsc} />
              <InfoRow label="Branch"         value={user.bank_account.branch} />
            </div>
          ) : (
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>Not provided</p>
          )}
        </Section>

        {/* ── Nominee ── */}
        <Section icon={ShieldCheck} title="Nominee">
          {user.nominee ? (
            <div className="flex flex-col gap-2.5">
              <InfoRow label="Name"         value={user.nominee.name} />
              <InfoRow label="Age"          value={user.nominee.age} />
              <InfoRow label="Mobile"       value={user.nominee.mobile} />
              <InfoRow label="Aadhaar Card" value={user.nominee.aadhar_card} />
            </div>
          ) : (
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>Not provided</p>
          )}
        </Section>

        {/* ── ID Proofs ── */}
        <Section icon={FileText} title="ID Proofs">
          {user.id_proofs && user.id_proofs.length > 0 ? (
            <div className="flex flex-col gap-2">
              {user.id_proofs.map((proof) => (
                <a key={proof.id} href={proof.document_url}
                   target="_blank" rel="noopener noreferrer"
                   className="flex items-center justify-between rounded-xl px-3 py-2.5 transition-opacity hover:opacity-80"
                   style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <span className="text-xs font-medium text-white">{docLabel(proof.document_type)}</span>
                  <ExternalLink size={13} style={{ color: '#d4a017' }} />
                </a>
              ))}
            </div>
          ) : (
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>No documents uploaded</p>
          )}
        </Section>

      </div>

      {/* ── Add Profit Sheet ── */}
      {showProfit && (
        <AddProfitSheet
          user={user}
          onClose={() => setShowProfit(false)}
          onSuccess={handleProfitSuccess}
        />
      )}
    </div>
  );
}
